import iconsJson from "@/mappings/entity-icons.json"
import {
  DetailedEntity,
  Entity,
  isEventActorRef,
  isInspirationRef,
  isPlaceRef,
} from "@/types/entity"
import { EventActor } from "@/types/event-actor"
import { AirportTransitPoint } from "@/types/flights/transit-points"
import { Inspiration } from "@/types/inspiration"
import { Organization } from "@/types/organization"
import { Place, PlacePlaceholder, PlaceSummary, PlaceType } from "@/types/place"
import { WebSearchResult } from "@/types/web-search-result"

import {
  inspirationToAmplitudeProperties,
  placeToAmplitudeProperties,
} from "@/lib/analytics/amplitude-properties"
import { AmplitudeEventAndProperties } from "@/lib/analytics/amplitude-types"
import { isMicrositeHost } from "@/lib/is-embedded"
import { freeze } from "@/lib/utils/collections"
import { BASE_CANONICAL_URL } from "@/lib/utils/constants"
import { blank, present } from "@/lib/utils/presence"
import type { IconName } from "@/components/icons/icon-names"

export function getAllPlaceTypes() {
  if (!_allPlaceTypes)
    _allPlaceTypes = freeze([
      "hotel",
      "restaurant",
      "event",
      "attraction",
      "activity",
      "location",
    ])
  return _allPlaceTypes
}

let _allPlaceTypes: PlaceType[] | undefined

export function isHotel(place: Place | PlacePlaceholder) {
  return place.type === "hotel"
}

export function isRestaurant(place: Place | PlacePlaceholder) {
  return place.type === "restaurant"
}

export function isReservableRestaurant(place: Place) {
  return place.type === "restaurant" && place.reservable === true
}

export function isEvent(place: Place | PlacePlaceholder) {
  return place.type === "event"
}

export function isAttraction(place: Place | PlacePlaceholder) {
  return place.type === "attraction"
}

export function isActivity(place: Place | PlacePlaceholder) {
  return place.type === "activity"
}

export function isLocation(place: Place | PlacePlaceholder) {
  return place.type === "location"
}

export function isAirport(place: Place | PlacePlaceholder) {
  return place.type === "airport"
}

export function isCountry(place: Place) {
  return isLocation(place) && place.category_tier === "country_tier"
}

export function isIsland(place: Place) {
  return isLocation(place) && place.category === "island"
}

export function isState(place: Place) {
  return isLocation(place) && place.category === "state"
}

export function isProvince(place: Place) {
  return isLocation(place) && place.category === "province"
}

export function isRegion(place: Place) {
  return isLocation(place) && place.category_tier === "region_tier"
}

export function isArea(place: Place) {
  return isLocation(place) && place.category === "area"
}

export function isNationalPark(place: Place) {
  return isLocation(place) && place.category === "national park"
}

export function isCity(place: Place) {
  return isLocation(place) && place.category_tier === "city_tier"
}

export function isNeighborhood(place: Place) {
  return isLocation(place) && place.category_tier === "neighborhood_tier"
}

export function isContinent(place: Place) {
  return isLocation(place) && place.category_tier === "continent_tier"
}

export function isSubcontinent(place: Place) {
  return isLocation(place) && place.category === "subcontinent"
}

export function isTerritory(place: Place) {
  return isLocation(place) && place.category === "territory"
}

export function placeIsInList(place: Place, places: Place[]): boolean {
  return places.some((sp) => sp.entity_ref === place.entity_ref)
}

export function isPlacePlaceholder(
  place: Place | PlacePlaceholder
): place is PlacePlaceholder {
  return !("canonical_url" in place)
}

export function toPlacePlaceholder(place: Place): PlacePlaceholder | undefined {
  if (place.latitude == null || place.longitude == null) return undefined

  switch (place.type) {
    case "hotel":
      return {
        type: "hotel",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        categories: place.categories ?? [],
      }
    case "restaurant":
      return {
        type: "restaurant",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        categories: place.categories ?? [],
      }
    case "event":
      return {
        type: "event",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        categories: place.categories ?? [],
        venue_name: place.venue_name,
      }
    case "attraction":
      return {
        type: "attraction",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        categories: place.categories ?? [],
      }
    case "activity":
      return {
        type: "activity",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        categories: place.categories ?? [],
      }
    case "location":
      return {
        type: "location",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        category: place.category,
        categories: place.categories ?? [],
      }
    case "airport":
      return {
        type: "airport",
        entity_ref: place.entity_ref,
        name: place.name,
        latitude: place.latitude,
        longitude: place.longitude,
        code: place.code,
      }
  }
}

export const inspirationRefPrefix = "in"

export function transitPointForPlace(
  place: PlaceSummary
): AirportTransitPoint | undefined {
  return isAirport(place)
    ? {
        type: "airport",
        airport: place,
        id: place.entity_ref,
        time_zone: place.time_zone,
        latitude: place.latitude,
        longitude: place.longitude,
        region: place.region || "",
        country_code: place.country_code || "",
        code: place.code,
        name: place.name,
        city: place.city || "",
        country: place.country || "",
      }
    : undefined
}

export function buildPlaceDirectionsAddress(place: Place): string {
  let addressComponents

  if (isLocation(place)) {
    addressComponents = [place.name]
    if (place.city && !isCity(place)) {
      addressComponents.push(place.city)
    }

    if (place.state && !isState(place)) {
      addressComponents.push(place.state)
    }

    if (
      place.region &&
      place.region !== place.state &&
      !isRegion(place) &&
      !isNeighborhood(place)
    ) {
      addressComponents.push(place.region)
    }

    // directions fail with "United States"
    if (place.country && !isCountry(place) && place.country_code !== "US") {
      addressComponents.push(place.country)
    }
  } else {
    addressComponents = [
      !isActivity(place) ? place.name : undefined,
      // directions fail with "USA" or "United States"
      place.formatted_address?.replace("USA", "").replace("United States", ""),
    ]
  }

  return addressComponents.filter(present).map(encodeURIComponent).join(",")
}

const googleDirectionsBaseUrl = "https://www.google.com/maps/dir/?api=1"

function buildGoogleDirectionsLink(
  destination: string,
  origin?: string
): string {
  const parts = [googleDirectionsBaseUrl]

  if (origin) parts.push(`&origin=${origin}`)
  parts.push(`&destination=${destination}`)

  return `${parts.join("")}`
}

export function googleDirectionsLink(
  destination: Place,
  origin?: Place
): string {
  return buildGoogleDirectionsLink(
    buildPlaceDirectionsAddress(destination),
    origin ? buildPlaceDirectionsAddress(origin) : undefined
  )
}

export function googleDirectionsLinkFromText(
  destination: string,
  origin?: string
): string {
  return buildGoogleDirectionsLink(
    encodeURIComponent(destination),
    origin ? encodeURIComponent(origin) : undefined
  )
}

export function formatAddress(place: Place, stacked = false) {
  const address = place.formatted_address
  if (!stacked) return address
  if (!address) return undefined

  const parts = address.split(", ")
  if (parts.length === 3) {
    return `${parts[0]}\n${parts[1]}\n${parts[2]}`
  } else if (parts.length > 3) {
    const country = parts.pop()
    const postalAndState = parts.pop()
    const city = parts.pop()
    const street = parts.join(", ")

    return `${street}\n${city}, ${postalAndState}\n${country}`
  } else {
    return address.replace(/, /g, "\n")
  }
}

export function hasPricelineProvider(place: Place) {
  return !!place.providers?.includes("priceline")
}

export function hasNuiteeProvider(place: Place) {
  return !!place.providers?.includes("nuitee")
}

export function hasEmbeddedProvider(place: Place) {
  return hasPricelineProvider(place) || hasNuiteeProvider(place)
}

export function hasAnyHotelProvider(place: Place) {
  return (
    !!place.providers?.includes("priceline") ||
    !!place.providers?.includes("nuitee") ||
    !!place.providers?.includes("expedia") ||
    !!place.providers?.includes("vio") ||
    !!place.providers?.includes("kayak")
  )
}

export function hasViatorProvider(place: Place) {
  return !!place.providers?.includes("viator")
}

export function entityIsGuide(
  entity: Entity | undefined
): entity is Inspiration {
  const entityRef = entity?.entity_ref
  return entityRef ? isInspirationRef(entityRef) : false
}

export function entityIsPlace(entity: Entity | undefined): entity is Place {
  const entityRef = entity?.entity_ref
  return entityRef ? isPlaceRef(entityRef) : false
}

export function entityIsEventActor(
  entity: Entity | undefined
): entity is EventActor {
  const entityRef = entity?.entity_ref
  return entityRef ? isEventActorRef(entityRef) : false
}

export function isDetailedEntity(entity: Entity): entity is DetailedEntity {
  return entity.detailed
}

export function getWords(
  text: string,
  count: number,
  totalTextLength?: number
) {
  if (blank(text)) return ""

  // truncate text if totalTextLength is provided
  const truncatedText = totalTextLength ? text.slice(0, totalTextLength) : text

  const words = truncatedText.match(/\b\w+\b/g)
  if (words && words.length > 0) {
    return words
      .slice(0, count)
      .map((word) => word.trim())
      .join(" ")
  } else {
    return text
  }
}

export function getSentences(text: string, count: number) {
  if (blank(text)) return ""
  const sentences = text.match(/[^.!?]+[.!?]+/g)
  if (sentences && sentences.length > 0) {
    return sentences
      .slice(0, count)
      .map((sentence) => sentence.trim())
      .join(" ")
  } else {
    return text
  }
}

export function getFirstSentence(text: string) {
  return getSentences(text, 1)
}

export function getFirstTwoSentences(text: string) {
  return getSentences(text, 2)
}

export function buildAmplitudeEvent(
  entity: Entity,
  target: string,
  emails?: string[],
  note?: string
) {
  let ampEvent: AmplitudeEventAndProperties
  if (entityIsPlace(entity)) {
    ampEvent = {
      name: "PlaceShared",
      properties: {
        target: target,
        emails: emails,
        note: note,
        place: placeToAmplitudeProperties(entity).location,
      },
    }
  } else if (entityIsGuide(entity)) {
    ampEvent = {
      name: "InspirationShared",
      properties: {
        target: target,
        emails: emails,
        note: note,
        inspiration: inspirationToAmplitudeProperties(entity),
      },
    }
  } else {
    ampEvent = {
      name: "EventActorShared",
      properties: {
        target: target,
        emails: emails,
        note: note,
        event_actor: {
          entity_ref: entity.entity_ref,
          name: entity.name,
        },
      },
    }
  }
  return ampEvent
}

export function relativeCanonicalUrl(url: string): string {
  return url?.replace(BASE_CANONICAL_URL, "") || ""
}

export function addOrganizationHostToUrl(
  url: string,
  org?: Organization
): string {
  // see similar logic in OrganizationService.rb
  if (org) {
    const relativePath = url.replace(BASE_CANONICAL_URL, "")
    if (isMicrositeHost()) {
      // microsite: link directly to subdomain
      return `https://${org.orgid}.mindtrip.ai${relativePath}`
    }
    return `${org.website_url}?mt:url=${encodeURIComponent(relativePath)}`
  }
  return url
}

export function isPlaceClosed(place: Place) {
  return (
    present(place.status) &&
    (place.status === "CLOSED" || place.status === "TEMPORARILY_CLOSED")
  )
}

const iconMap = iconsJson as Record<string, IconName>

function getIconNameFromCategory(category: string): IconName | null {
  return iconMap[category] || null
}

export function getSearchResultIconName(
  searchResult: WebSearchResult
): IconName {
  const isTrail = searchResult.type === "trail"
  const isEvent = searchResult.type === "event"

  if (
    isEvent &&
    searchResult.categories &&
    searchResult.categories.length > 0
  ) {
    for (const category of searchResult.categories) {
      const iconName = getIconNameFromCategory(category)
      if (iconName) return iconName
    }
  }

  return isTrail ? "trail" : "event"
}

/**
 * Extracts entity type from customer_purpose string
 * @param customer_purpose e.g., "recommendations:restaurant" or "recommendations"
 * @returns entity type like "restaurant", "hotel", "activity", or null if no specific type
 */
export function parseEntityTypeFromCustomerPurpose(
  customer_purpose: string | undefined
): PlaceType | null {
  if (!customer_purpose) return null

  const parts = customer_purpose.split(":")
  if (parts.length < 2) return null

  const entityType = parts[1]

  // map backend types to frontend PlaceType
  const typeMap: Record<string, PlaceType> = {
    hotel: "hotel",
    restaurant: "restaurant",
    activity: "activity",
    attraction: "attraction",
    event: "event",
  }

  return typeMap[entityType] || null
}

/**
 * Filters places by entity type from customer_purpose
 * @param places array of places to filter
 * @param customer_purpose e.g., "recommendations:restaurant"
 * @returns filtered array of places, or original array if no specific type in customer_purpose
 */
export function filterPlacesByCustomerPurpose(
  places: Place[],
  customer_purpose: string | undefined
): Place[] {
  const entityType = parseEntityTypeFromCustomerPurpose(customer_purpose)

  // if customer_purpose specifies an entity type, filter to only that type
  if (entityType) {
    return places.filter((p) => p.type === entityType)
  }

  // otherwise return all places (no filtering)
  return places
}
